// Import model
import { WoodCatalogItem } from '../models/WoodCatalogItem';

// Import base class serializer
import { CatalogItemSerializer } from './CatalogItemSerializer';

// Import dependency types (models and enums used in properties)
import { OtgItemCost } from '../models/OtgItemCost';
import { OtgMeasurement } from '../models/OtgMeasurement';
import { OtgUomRate } from '../models/OtgUomRate';
import { SearchMetaData } from '../models/SearchMetaData';

// Import dependency serializers (for complex types)
import { OtgItemCostSerializer } from './OtgItemCostSerializer';
import { OtgMeasurementSerializer } from './OtgMeasurementSerializer';
import { OtgUomRateSerializer } from './OtgUomRateSerializer';
import { SearchMetaDataSerializer } from './SearchMetaDataSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for WoodCatalogItem - handles JSON serialization and deserialization with type validation
 */
export class WoodCatalogItemSerializer {
  /**
   * Deserializes a JSON string into a WoodCatalogItem instance
   */
  static deserialize(json: string): WoodCatalogItem {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a WoodCatalogItem instance
   */
  static fromObject(obj: any): WoodCatalogItem {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to WoodCatalogItem');
    }


    const instance: WoodCatalogItem = {
      id: obj['_id'] as string | undefined,
      abbreviatedName: obj['abbreviatedName'] as string | undefined,
      backing: obj['backing'] as string | undefined,
      buyingGroup: obj['buyingGroup'] as string | undefined,
      catalogFeedId: obj['catalogFeedId'] as string | undefined,
      catalogFeedIdName: obj['catalogFeedId|Name'] as string | undefined,
      collectionName: obj['collectionName'] as string | undefined,
      colorName: obj['colorName'] as string | undefined,
      colorNumber: obj['colorNumber'] as string | undefined,
      composition: obj['composition'] as string | undefined,
      contract: obj['contract'] as string | undefined,
      description: obj['description'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      name: obj['name'] as string | undefined,
      organization: obj['organization'] as string | undefined,
      organizationName: obj['organization|Name'] as string | undefined,
      gauge: obj['gauge'] as string | undefined,
      manufacturer: obj['manufacturer'] as string | undefined,
      manufacturerSku: obj['manufacturerSKU'] as string | undefined,
      manufacturerStyleName: obj['manufacturerStyleName'] as string | undefined,
      manufacturerStyleNumber: obj['manufacturerStyleNumber'] as string | undefined,
      materialSubType: obj['materialSubType'] as string | undefined,
      materialType: obj['materialType'] as string,
      pricingGroup: obj['pricingGroup'] as string | undefined,
      primaryComponent: obj['primaryComponent'] as string | undefined,
      productSpecification: obj['productSpecification'] as string | undefined,
      productType: obj['productType'] as string | undefined,
      sellingCompany: obj['sellingCompany'] as string | undefined,
      sizeCode: obj['sizeCode'] as string | undefined,
      sku: obj['sku'] as string | undefined,
      styleName: obj['styleName'] as string | undefined,
      styleNumber: obj['styleNumber'] as string | undefined,
      uniqueColorCode: obj['uniqueColorCode'] as string | undefined,
      upcCode: obj['upcCode'] as string | undefined,
      manufacturerId: obj['manufacturerId'] as string | undefined,
      manufacturerIdName: obj['manufacturerId|Name'] as string | undefined,
      carpetType: obj['carpetType'] as string | undefined,
      collectionNameOverride: obj['collectionNameOverride'] as string | undefined,
      colorType: obj['colorType'] as string | undefined,
      competitionCrushers: obj['competitionCrushers'] as boolean | undefined,
      demo: obj['demo'] as string | undefined,
      durabilityRating: obj['durabilityRating'] as number | undefined,
      fiber: obj['fiber'] as string | undefined,
      installationMethod: obj['installationMethod'] as string | undefined,
      laminatesType: obj['laminatesType'] as string | undefined,
      styleLength: obj['styleLength'] as string | undefined,
      links: obj['links'] as string | undefined,
      stylePatternMatch: obj['stylePatternMatch'] as string | undefined,
      plankLength: obj['plankLength'] as string | undefined,
      priceGroup: obj['priceGroup'] as number | undefined,
      resilientType: obj['resilientType'] as string | undefined,
      stainResistanceRating: obj['stainResistanceRating'] as number | undefined,
      styleNameOverride: obj['styleNameOverride'] as string | undefined,
      texture: obj['texture'] as string | undefined,
      styleThickness: obj['styleThickness'] as string | undefined,
      vendor: obj['vendor'] as string | undefined,
      wearLayer: obj['wearLayer'] as string | undefined,
      styleWidth: obj['styleWidth'] as string | undefined,
      woodSpecies: obj['woodSpecies'] as string | undefined,
      woodType: obj['woodType'] as string | undefined,
      searchMetaData: obj['searchMetaData'] ? SearchMetaDataSerializer.fromObject(obj['searchMetaData']) : undefined as any,
      tags: obj['tags'] ? (obj['tags'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      sellingUnitUom: obj['sellingUnitUOM'] as string | undefined,
      sellingUnitQtty: obj['sellingUnitQtty'] as number | undefined,
      standardPrice: obj['standardPrice'] as number | undefined,
      palletPrice: obj['palletPrice'] as number | undefined,
      cutPrice: obj['cutPrice'] as number | undefined,
      patternRepeat: obj['patternRepeat'] ? OtgMeasurementSerializer.fromObject(obj['patternRepeat']) : undefined as any,
      patternDrop: obj['patternDrop'] ? OtgMeasurementSerializer.fromObject(obj['patternDrop']) : undefined as any,
      patternLength: obj['patternLength'] ? OtgMeasurementSerializer.fromObject(obj['patternLength']) : undefined as any,
      patternWidth: obj['patternWidth'] ? OtgMeasurementSerializer.fromObject(obj['patternWidth']) : undefined as any,
      nominalLength: obj['nominalLength'] ? OtgMeasurementSerializer.fromObject(obj['nominalLength']) : undefined as any,
      actualLength: obj['actualLength'] ? OtgMeasurementSerializer.fromObject(obj['actualLength']) : undefined as any,
      nominalWidth: obj['nominalWidth'] ? OtgMeasurementSerializer.fromObject(obj['nominalWidth']) : undefined as any,
      actualWidth: obj['actualWidth'] ? OtgMeasurementSerializer.fromObject(obj['actualWidth']) : undefined as any,
      height: obj['height'] ? OtgMeasurementSerializer.fromObject(obj['height']) : undefined as any,
      faceWeight: obj['faceWeight'] ? OtgMeasurementSerializer.fromObject(obj['faceWeight']) : undefined as any,
      effectiveDate: obj['effectiveDate'] as string | undefined,
      pendingDate: obj['pendingDate'] as string | undefined,
      droppedDate: obj['droppedDate'] as string | undefined,
      promotionalStartDate: obj['promotionalStartDate'] as string | undefined,
      promotionalEndDate: obj['promotionalEndDate'] as string | undefined,
      costs: obj['costs'] ? (obj['costs'] as any[]).map((item: any) =>
        OtgItemCostSerializer.fromObject(item)
      ) : [] as any,
      margin: obj['margin'] as number | undefined,
      rollLength: obj['rollLength'] as number | undefined,
      rollWidth: obj['rollWidth'] as number | undefined,
      packaging: obj['packaging'] ? (obj['packaging'] as any[]).map((item: any) =>
        OtgUomRateSerializer.fromObject(item)
      ) : [] as any,
      shippingWeight: obj['shippingWeight'] ? OtgUomRateSerializer.fromObject(obj['shippingWeight']) : undefined as any,
      prices: obj['prices'] ? (obj['prices'] as any[]).map((item: any) =>
        OtgItemCostSerializer.fromObject(item)
      ) : [] as any,
      sellingUnit: obj['sellingUnit'] ? OtgMeasurementSerializer.fromObject(obj['sellingUnit']) : undefined as any,
      _04850457: obj['_04850457'] as any | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of WoodCatalogItem instances
   */
  static deserializeArray(json: string): WoodCatalogItem[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a WoodCatalogItem instance to a JSON string
   */
  static serialize(instance: WoodCatalogItem): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a WoodCatalogItem instance to a plain object
   */
  static toObject(instance: WoodCatalogItem): any {
    return {
      '_id': instance.id,
      'abbreviatedName': instance.abbreviatedName,
      'backing': instance.backing,
      'buyingGroup': instance.buyingGroup,
      'catalogFeedId': instance.catalogFeedId,
      'catalogFeedId|Name': instance.catalogFeedIdName,
      'collectionName': instance.collectionName,
      'colorName': instance.colorName,
      'colorNumber': instance.colorNumber,
      'composition': instance.composition,
      'contract': instance.contract,
      'description': instance.description,
      'isActive': instance.isActive,
      'name': instance.name,
      'organization': instance.organization,
      'organization|Name': instance.organizationName,
      'gauge': instance.gauge,
      'manufacturer': instance.manufacturer,
      'manufacturerSKU': instance.manufacturerSku,
      'manufacturerStyleName': instance.manufacturerStyleName,
      'manufacturerStyleNumber': instance.manufacturerStyleNumber,
      'materialSubType': instance.materialSubType,
      'materialType': instance.materialType,
      'pricingGroup': instance.pricingGroup,
      'primaryComponent': instance.primaryComponent,
      'productSpecification': instance.productSpecification,
      'productType': instance.productType,
      'sellingCompany': instance.sellingCompany,
      'sizeCode': instance.sizeCode,
      'sku': instance.sku,
      'styleName': instance.styleName,
      'styleNumber': instance.styleNumber,
      'uniqueColorCode': instance.uniqueColorCode,
      'upcCode': instance.upcCode,
      'manufacturerId': instance.manufacturerId,
      'manufacturerId|Name': instance.manufacturerIdName,
      'carpetType': instance.carpetType,
      'collectionNameOverride': instance.collectionNameOverride,
      'colorType': instance.colorType,
      'competitionCrushers': instance.competitionCrushers,
      'demo': instance.demo,
      'durabilityRating': instance.durabilityRating,
      'fiber': instance.fiber,
      'installationMethod': instance.installationMethod,
      'laminatesType': instance.laminatesType,
      'styleLength': instance.styleLength,
      'links': instance.links,
      'stylePatternMatch': instance.stylePatternMatch,
      'plankLength': instance.plankLength,
      'priceGroup': instance.priceGroup,
      'resilientType': instance.resilientType,
      'stainResistanceRating': instance.stainResistanceRating,
      'styleNameOverride': instance.styleNameOverride,
      'texture': instance.texture,
      'styleThickness': instance.styleThickness,
      'vendor': instance.vendor,
      'wearLayer': instance.wearLayer,
      'styleWidth': instance.styleWidth,
      'woodSpecies': instance.woodSpecies,
      'woodType': instance.woodType,
      'searchMetaData': instance.searchMetaData ? SearchMetaDataSerializer.toObject(instance.searchMetaData) : undefined,
      'tags': instance.tags?.map((item: any) =>
        item
      ),
      'sellingUnitUOM': instance.sellingUnitUom,
      'sellingUnitQtty': instance.sellingUnitQtty,
      'standardPrice': instance.standardPrice,
      'palletPrice': instance.palletPrice,
      'cutPrice': instance.cutPrice,
      'patternRepeat': instance.patternRepeat ? OtgMeasurementSerializer.toObject(instance.patternRepeat) : undefined,
      'patternDrop': instance.patternDrop ? OtgMeasurementSerializer.toObject(instance.patternDrop) : undefined,
      'patternLength': instance.patternLength ? OtgMeasurementSerializer.toObject(instance.patternLength) : undefined,
      'patternWidth': instance.patternWidth ? OtgMeasurementSerializer.toObject(instance.patternWidth) : undefined,
      'nominalLength': instance.nominalLength ? OtgMeasurementSerializer.toObject(instance.nominalLength) : undefined,
      'actualLength': instance.actualLength ? OtgMeasurementSerializer.toObject(instance.actualLength) : undefined,
      'nominalWidth': instance.nominalWidth ? OtgMeasurementSerializer.toObject(instance.nominalWidth) : undefined,
      'actualWidth': instance.actualWidth ? OtgMeasurementSerializer.toObject(instance.actualWidth) : undefined,
      'height': instance.height ? OtgMeasurementSerializer.toObject(instance.height) : undefined,
      'faceWeight': instance.faceWeight ? OtgMeasurementSerializer.toObject(instance.faceWeight) : undefined,
      'effectiveDate': instance.effectiveDate,
      'pendingDate': instance.pendingDate,
      'droppedDate': instance.droppedDate,
      'promotionalStartDate': instance.promotionalStartDate,
      'promotionalEndDate': instance.promotionalEndDate,
      'costs': instance.costs?.map((item: any) =>
        OtgItemCostSerializer.toObject(item)
      ),
      'margin': instance.margin,
      'rollLength': instance.rollLength,
      'rollWidth': instance.rollWidth,
      'packaging': instance.packaging?.map((item: any) =>
        OtgUomRateSerializer.toObject(item)
      ),
      'shippingWeight': instance.shippingWeight ? OtgUomRateSerializer.toObject(instance.shippingWeight) : undefined,
      'prices': instance.prices?.map((item: any) =>
        OtgItemCostSerializer.toObject(item)
      ),
      'sellingUnit': instance.sellingUnit ? OtgMeasurementSerializer.toObject(instance.sellingUnit) : undefined,
      '_04850457': instance._04850457,
    };
  }

  /**
   * Serializes an array of WoodCatalogItem instances to a JSON string
   */
  static serializeArray(instances: WoodCatalogItem[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
